import { Box, Group } from "@mantine/core";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Badge } from "#components/atoms/Badge/Badge";
import { Divider } from "#components/atoms/Divider/Divider";
import { Text } from "#components/atoms/Typographies/Text";
import { SearchResult } from "#modules/search/infra/search.api";
import classes from "#modules/search/view/SearchResults.module.css";
import { Icon } from "#shared/icons/Icon";
import { assertUnreachable, formatDate, formatMaterialDimensions, SearchEntity } from "shared";

const previewStyle = {
  width: 48,
  height: 48,
  borderRadius: radius.xs,
  overflow: "hidden",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: colors.bg.elevation.quaternary,
} as const;

const QuoteRequestPreview = () => (
  <Box style={previewStyle}>
    <Icon name="invoice" size={20} color={colors.icon.secondary} />
  </Box>
);

const PartPreview = ({ src }: { src: string | null }) => (
  <Box style={{ ...previewStyle, border: `1px solid ${colors.border.quaternary}` }}>
    {src ? (
      <img
        src={src}
        alt=""
        width={48}
        height={48}
        style={{ width: 48, height: 48, objectFit: "cover" }}
      />
    ) : null}
  </Box>
);

const ResultText = ({ title, subtitle }: { title: string; subtitle: React.ReactNode }) => (
  <Box style={{ flex: 1, minWidth: 0 }} px={spacing[2]} py={spacing[0.5]}>
    <Text size="small" type="medium" truncate>
      {title}
    </Text>
    {subtitle}
  </Box>
);

const ResultDate = ({ date }: { date: string | Date }) => (
  <Text
    size="small"
    type="regular"
    color={colors.text.secondaryDefault}
    style={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
  >
    {formatDate(date)}
  </Text>
);

const rowStyle = {
  "--row-py": spacing[3],
  "--row-hover-bg": colors.bg.elevation.secondary,
} as React.CSSProperties;

const QuoteRequestResult = ({
  result,
}: {
  result: Extract<SearchResult, { type: typeof SearchEntity.QuoteRequest }>;
}) => {
  const { t } = useTranslation();
  const { quoteRequest, clientName, partCount } = result;
  const subtitle = clientName
    ? t("quoteRequestsV2.table.content.name", { clientName, count: partCount })
    : t("quoteRequestsV2.table.content.nameNoClient", { count: partCount });

  return (
    <Link to={`/v2/quote-requests/${quoteRequest.id}`} className={classes.row} style={rowStyle}>
      <Group gap={spacing[2]} wrap="nowrap" style={{ flex: 1, minWidth: 0 }} pl={spacing[2]}>
        <QuoteRequestPreview />
        <ResultText
          title={quoteRequest.reference ?? quoteRequest.clientReference ?? ""}
          subtitle={
            <Text size="small" type="regular" color={colors.text.secondaryDefault} truncate>
              {subtitle}
            </Text>
          }
        />
      </Group>
      <Box px={spacing[2]}>
        <ResultDate date={quoteRequest.updatedAt} />
      </Box>
    </Link>
  );
};

const PartResult = ({
  result,
}: {
  result: Extract<SearchResult, { type: typeof SearchEntity.QuoteRequestLine }>;
}) => {
  const { t } = useTranslation();
  const { quoteRequestLine } = result;
  const { part } = quoteRequestLine;
  const typeLabel = part.type ? t(`part.type.${part.type}`) : null;
  const dimensions = formatMaterialDimensions(part.type, {
    height: part.height,
    width: part.width,
    length: part.length,
    tubeThickness: part.tubeThickness,
  });

  return (
    <Link
      to={`/v2/quote-requests/${quoteRequestLine.quoteRequestId}`}
      className={classes.row}
      style={rowStyle}
    >
      <Group gap={spacing[2]} wrap="nowrap" style={{ flex: 1, minWidth: 0 }} pl={spacing[2]}>
        <PartPreview src={part.urlPng3D ?? part.urlPng2D ?? null} />
        <ResultText
          title={part.name}
          subtitle={
            <Group gap={spacing[2]} wrap="nowrap">
              {typeLabel ? (
                <Badge variant="neutral" shape="squared">
                  {typeLabel}
                </Badge>
              ) : null}
              {dimensions ? (
                <Text size="tiny" type="regular" color={colors.text.secondaryDefault} truncate>
                  {dimensions}
                </Text>
              ) : null}
            </Group>
          }
        />
      </Group>
      <Box px={spacing[2]}>
        <ResultDate date={quoteRequestLine.updatedAt} />
      </Box>
    </Link>
  );
};

const SearchResultRow = ({ result }: { result: SearchResult }) => {
  const type = result.type;
  switch (type) {
    case SearchEntity.QuoteRequest:
      return <QuoteRequestResult result={result} />;
    case SearchEntity.QuoteRequestLine:
      return <PartResult result={result} />;
    default:
      return assertUnreachable(type);
  }
};

export const SearchResultsList = ({ results }: { results: SearchResult[] }) => {
  if (results.length === 0) {
    return null;
  }

  return (
    <Box>
      {results.map((result) => {
        const id =
          result.type === SearchEntity.QuoteRequest
            ? result.quoteRequest.id
            : result.quoteRequestLine.id;

        return (
          <Fragment key={id}>
            <Divider type="secondary" />
            <SearchResultRow result={result} />
          </Fragment>
        );
      })}
      <Divider type="secondary" />
    </Box>
  );
};
